import ModuleDashboard from '#/store/module/dashboard.vuex';
import ModuleStoryVersions from '#/store/module/storyVersions.vuex';
import ModuleVideo from '#/store/module/video.vuex';
import '#/store/module/masthead.vuex';
import '#/store/module/story.vuex';
import '#/store/module/video.vuex';
import UserModule from '#/store/module/user.vuex';
import ModuleMasthead from '#/store/module/masthead.vuex';
import UserVisibilityPlugin from 'store/plugins/UserVisibilityPlugin';
import { MaintenancePlugin } from 'store/plugins/MaintenancePlugin';
import { AspectRatiosController } from 'c/AspectRatiosController';
import { UNCATEGORIZED_FLAG } from '#/const';

export default ({ state }) => ({
  state: {
    /**
     * reflects `Backbone.history.fragment`
     * @type {string|null}
     */
    routePath: null,
    /**
     * What was the routePath when user entered/started the story
     * so when he returns from story we navigate him to that path
     * @type {string|null}
     */
    backNavigablePath: null,
    /**
     * dashboard analytics statusCode and url (if available)
     * @type {{url, statusCode}}
     */
    analyticsData: {
      url: '',
      statusCode: null,
    },

    maintenanceState: {
      data: {
        lock: false,
        lockTimestamp: null,
        version: null,
        versionTimestamp: null,
      },
    },

    currentTime: Date.now(),

    pauseRequestedAt: 0,

    storyboardCategories: [],
    channelOwnedCategories: [],
    inheritedChannelCategories: [],
    numberOfUncategorizedTemplates: null,

    UserVisibility_isActive: true,
    UserSession_isActive: false,
  },

  modules: {
    masthead: ModuleMasthead,
    dashboard: ModuleDashboard,
    storyVersions: ModuleStoryVersions,
    video: ModuleVideo,
    user: UserModule(state.user),
  },

  // @params state, getters
  getters: {
    routePath: (state) => state.routePath,
    analyticsData: (state) => state.analyticsData,
    storyboardCategoryById: (state) => (id) => {
      return _.find(state.storyboardCategories, { id });
    },

    categoryCommonwealth(state) {
      return [
        ...state.storyboardCategories,
        ...state.channelOwnedCategories,
        ...state.inheritedChannelCategories,
      ];
    },

    hasMoreThenOneProgrammer() {
      return !app.programmers.isSingle();
    },

    firstProgrammerId() {
      return app.programmers.isSingle() ? app.programmers.first().id : null;
    },

    userId() {
      return app.rootStore.getters['user/id'];
    },

    hasPermission: () => (permission) => {
      return app.rootStore.getters['user/hasPermission'](permission);
    },

    isUserCanCreateStories(state, getters) {
      return getters.hasPermission(app.ROLES.STORY_CREATION);
    },

    inStory(state) {
      return !!state.story;
    },
  },

  // @params state, payload
  mutations: {
    onRouteChanged(state, path) {
      state.routePath = path.replace(/(.*)\?.*$/, '$1');

      if (/^dashboard\//.test(state.routePath)) {
        state.backNavigablePath = state.routePath;
      }
    },

    setAnalyticsData(state, data) {
      state.analyticsData.url = data.url;
      state.analyticsData.statusCode = data.status;
    },

    setCurrentTime(state) {
      state.currentTime = Date.now();
    },

    setStoryboardCategories(state, arr) {
      state.storyboardCategories = arr;
    },

    setChannelOwnedCategories(state, arr) {
      state.channelOwnedCategories = arr;
    },

    setNumberOfUncategorizedTemplates(state, channelOwnedCategories) {
      const fakeCategory = channelOwnedCategories.find(
        (category) => category.id === UNCATEGORIZED_FLAG
      );
      if (fakeCategory) {
        state.numberOfUncategorizedTemplates = fakeCategory.numberOfTemplates;
      }
    },

    setInheritedChannelCategories(state, arr) {
      state.inheritedChannelCategories = arr;
    },

    requestPause(state) {
      state.pauseRequestedAt = Date.now();
    },

    setUserVisibility(state, isActive) {
      state.UserVisibility_isActive = isActive;
    },

    setUserSessionActivity(state, isActive) {
      state.UserSession_isActive = isActive;
    },

    setMaintenanceState(state, data) {
      state.maintenanceState.data = data;
    },
  },

  // @param context {{state, rootState, commit, dispatch, getters, rootGetters}}
  // @param payload
  actions: {
    $INIT_MODULE({ dispatch, getters, rootGetters }) {
      if (rootGetters['user/isImpededTemplatizer']) {
        AspectRatiosController.dropSupportForAspectRatio('ASPECT_4_5');
      }
    },

    navigate({ commit }, path) {
      commit('emptyFetchResults');
      commit('onRouteChanged', path);

      app.navigate(path);
    },

    startStoryFrom({ state }, { programmerId, aspectRatio, brandId, themeId }) {
      app.storyController.startNewStory({
        programmerId,
        storyStartPoint: app.storyStartPoint.MEDIA_SELECTION_NOVO,
        publishedDate: Date.now(),
        aspectRatio,
        brandId,
        themeId,
      });
    },

    FETCH_STORYBOARD_CATEGORIES(
      { commit, state, getters },
      { reset = false } = {}
    ) {
      return new Promise((resolve, reject) => {
        if (_.isEmpty(getters.categoryCommonwealth) || reset) {
          app.api.storyboards
            .getAllCategories()
            .then(
              ({
                categories,
                channelOwnedCategories,
                inheritedChannelCategories,
              }) => {
                commit('setStoryboardCategories', categories);
                commit(
                  'setNumberOfUncategorizedTemplates',
                  channelOwnedCategories
                );
                commit(
                  'setChannelOwnedCategories',
                  channelOwnedCategories.filter(
                    (category) => category.id !== UNCATEGORIZED_FLAG
                  )
                );
                commit(
                  'setInheritedChannelCategories',
                  inheritedChannelCategories
                );

                resolve();
              },
              reject
            );
        } else {
          resolve();
        }
      });
    },

    DELETE_STORYBOARD_CATEGORY({ commit, state }, categoryId) {
      const rv = new _.ColdPromise();
      app.api.storyboards
        .deleteCategory({
          categoryId,
        })
        .then(() => {
          commit('setChannelOwnedCategories', [
            ...state.channelOwnedCategories.filter((category) => {
              return category.id !== categoryId;
            }),
          ]);
          rv.resolve();
        })
        .catch((err) => {
          rv.reject(err);
        });
      return rv;
    },

    ADD_STORYBOARD_CATEGORY({ commit, state }, categoryName) {
      const rv = new _.ColdPromise();
      app.api.storyboards
        .postCategory({
          categoryName,
        })
        .then(({ id }) => {
          const categoryObj = {
            id,
            categoryName,
            categoryType: 'SEGMENT',
            numberOfTemplates: 0,
          };
          commit('setChannelOwnedCategories', [
            ...state.channelOwnedCategories,
            categoryObj,
          ]);
          rv.resolve(categoryObj);
        })
        .catch((err) => {
          rv.reject(err);
        });
      return rv;
    },

    RENAME_STORYBOARD_CATEGORY(
      { commit, state },
      { categoryId, categoryName }
    ) {
      const rv = new _.ColdPromise();
      app.api.storyboards
        .putCategory({
          categoryId,
          categoryName,
        })
        .then(() => {
          commit('setChannelOwnedCategories', [
            ...state.channelOwnedCategories.map((category) => {
              if (category.id === categoryId) {
                category.categoryName = categoryName;
              }
              return category;
            }),
          ]);
          rv.resolve();
        })
        .catch((err) => {
          rv.reject(err);
        });
      return rv;
    },

    ESCORT_UPLOADING_ASSET({ state, commit }, payload) {
      if (app.uploadDestination.ASSET_GALLERY === payload.uploadDestination) {
        if (state.assetGallery) {
          // check that assetGallery vuex module attached
          commit('addGalleryUploadingAsset', payload);
        }
      } else if (app.uploadDestination.LIGHTBOX === payload.uploadDestination) {
        if (payload.isNarration) {
          commit('addLightboxUploadingVoiceOverAsset', payload);
        } else {
          commit('addLightboxUploadingAsset', payload);
          if (state.assetGallery) {
            // check that assetGallery vuex module attached
            commit('addGalleryUploadingAsset', payload);
          }
        }
      }
    },
  },

  plugins: [
    // this plugin run <name-space>/$INIT_MODULE action for module initialization
    (store) => {
      Object.keys(store._actions)
        .filter((str) => str.endsWith('$INIT_MODULE'))
        .forEach((key) => {
          store.dispatch(key);
        });
    },
    UserVisibilityPlugin,
    MaintenancePlugin,
  ],
});
